import React, { useState } from "react";
import { Button, DatePicker, Form, Input } from "antd";
import dayjs from "dayjs";

import PlaceSearch from "../../Login/component/PlaceSearch";
import { controller } from "./controller";
import { addressFromPlace, buildNewPatient, formatDob, phoneDigits, readCreateResponse } from "./newPatient";

// Render the calendar inside the drawer: antd portals it to document.body by
// default, where it never opens over the drawer (the embedded policy form has the
// same fix).
function popupContainer(trigger) {
  return trigger.parentElement;
}

function nameOf(p) {
  return [p.first_name, p.last_name].filter(Boolean).join(" ");
}

/**
 * Step 1's way forward when the patient isn't in Smilepass yet — usually someone
 * the office has just added to its PMS, ahead of the nightly sync.
 *
 * Inline in the panel, like "Add a new policy" on step 2, not a modal over the
 * drawer. It asks for what a carrier verifies against and what the sync matches
 * on — both names and the date of birth — and nothing else is required. Phone,
 * email and the address are there to fill in when the office has them; the
 * address starts from the same Google search the onboarding and billing forms
 * use, and every part of it stays editable.
 *
 * The server checks the office's patients first. Someone with the same names and
 * date of birth comes back as a choice — "Use this patient" — instead of a second
 * record; "No, add a new patient" creates anyway, since two people can share both.
 *
 * @param {Object} initial   starting values — from the search ({firstName,
 *   lastName, phone, email}) or, as a draft to review, from an uploaded claim form
 *   (also birthDate "YYYY-MM-DD", address, city, state, zipCode); any of them
 * @param {string} [title]   the card's heading ("New patient" by default)
 * @param {string} [intro]   the line under it
 * @param {Function} onSelect  called with the patient to use, new or existing
 * @param {Function} onCancel  back to the search
 */
export default function NewPatientForm({ initial, onSelect, onCancel, title, intro }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // The 409's candidates, and the details that produced them (resent with force).
  const [matches, setMatches] = useState(null);
  const [pending, setPending] = useState(null);
  const start = initial || {};

  async function send(details, force) {
    setSaving(true);
    setError("");
    let res;
    try {
      res = await controller.createPatient(details, { force: force });
    } catch (e) {
      console.error(e);
      res = null;
    }
    setSaving(false);
    const outcome = readCreateResponse(res);
    if (outcome.kind === "created") {
      onSelect(outcome.patient);
    } else if (outcome.kind === "matches") {
      setPending(details);
      setMatches(outcome.candidates);
    } else if (outcome.kind === "fields") {
      setMatches(null);
      form.setFields(outcome.fields);
    } else {
      setError(outcome.message);
    }
  }

  function handleFinish(values) {
    send(buildNewPatient(values), false);
  }

  function handleAddress(parsed) {
    form.setFieldsValue(addressFromPlace(parsed));
  }

  const nameRules = function (what) {
    return [
      { required: true, whitespace: true, message: "Enter their " + what },
      { min: 2, message: "At least 2 letters" },
    ];
  };

  const phoneRule = {
    validator: function (_, value) {
      return !value || phoneDigits(value).length === 10
        ? Promise.resolve()
        : Promise.reject(new Error("Enter a 10-digit phone number"));
    },
  };

  return (
    <div className="ir-new-patient">
      {matches ? (
        <>
          <div className="ir-new-patient-head">Already in Smilepass?</div>
          <p className="ir-new-patient-sub">
            {matches.length === 1
              ? "A patient with this name and date of birth is already on your patient list."
              : matches.length + " patients with this name and date of birth are already on your patient list."}
          </p>
          {matches.map(function (m) {
            return (
              <div key={m.id} className="ir-match">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="ir-result-name">{nameOf(m) || "Unnamed patient"}</div>
                  <div className="ir-result-meta">
                    {formatDob(m.birth_date) || "No DOB"}
                    {m.phone ? " · " + m.phone : ""}
                    {m.from_pms ? " · From your PMS" : " · Added in Smilepass"}
                  </div>
                </div>
                <Button type="primary" onClick={function () { onSelect(m); }}>
                  Use this patient
                </Button>
              </div>
            );
          })}
          {error ? <div className="ir-new-patient-error" role="alert">{error}</div> : null}
          <div className="ir-new-patient-actions">
            <Button disabled={saving} onClick={function () { setMatches(null); }}>
              Back
            </Button>
            <Button loading={saving} onClick={function () { send(pending, true); }}>
              No, add a new patient
            </Button>
          </div>
        </>
      ) : null}

      {/* Hidden rather than unmounted while the matches are up, so "Back" returns
          to exactly what was typed. */}
      <div style={matches ? { display: "none" } : undefined}>
        <div className="ir-new-patient-head">{title || "New patient"}</div>
        <p className="ir-new-patient-sub">
          {intro || "For someone who isn't in Smilepass yet. Once your practice management "
            + "system syncs, we'll link them to their record there automatically."}
        </p>
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          disabled={saving}
          onFinish={handleFinish}
          initialValues={{
            first_name: start.firstName || "",
            last_name: start.lastName || "",
            birth_date: start.birthDate && dayjs(start.birthDate).isValid() ? dayjs(start.birthDate) : undefined,
            phone: start.phone || "",
            email: start.email || "",
            address: start.address || "",
            city: start.city || "",
            state: start.state || "",
            zip_code: start.zipCode || "",
          }}
        >
          <div className="ir-new-patient-grid">
            <Form.Item name="first_name" label="First name" rules={nameRules("first name")}>
              <Input autoComplete="off" autoFocus={!start.firstName} />
            </Form.Item>
            <Form.Item name="last_name" label="Last name" rules={nameRules("last name")}>
              <Input autoComplete="off" autoFocus={!!start.firstName && !start.lastName} />
            </Form.Item>
          </div>
          <Form.Item
            name="birth_date"
            label="Date of birth"
            rules={[{ required: true, message: "Enter their date of birth" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              placeholder="YYYY-MM-DD"
              getPopupContainer={popupContainer}
              disabledDate={function (d) { return !!d && d.isAfter(dayjs().endOf("day")); }}
            />
          </Form.Item>
          <div className="ir-new-patient-grid">
            <Form.Item name="phone" label="Phone" rules={[phoneRule]}>
              <Input autoComplete="off" inputMode="tel" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: "email", message: "Enter a valid email address" }]}
            >
              <Input autoComplete="off" />
            </Form.Item>
          </div>

          <div className="ir-new-patient-section">
            Address <span className="ir-new-patient-optional">(optional)</span>
          </div>
          {/* Enter picks a suggestion here. It must not also submit the form:
              the address is optional, so that would save the patient before the
              picked place has filled the fields in. */}
          <div
            className="ir-places"
            onKeyDown={function (e) { if (e.key === "Enter") e.preventDefault(); }}
          >
            <PlaceSearch onAddress={handleAddress} placeholder="Start typing their address…" />
          </div>
          <p className="ir-new-patient-hint">
            Pick it from the list and we'll fill in the rest, or type it in below.
          </p>
          <Form.Item name="address" label="Street address">
            <Input autoComplete="off" placeholder="120 Queen Street West" />
          </Form.Item>
          <div className="ir-new-patient-grid">
            <Form.Item name="city" label="City">
              <Input autoComplete="off" />
            </Form.Item>
            <Form.Item name="state" label="Province or state">
              <Input autoComplete="off" placeholder="ON" />
            </Form.Item>
          </div>
          <div className="ir-new-patient-grid">
            <Form.Item name="zip_code" label="Postal code">
              <Input autoComplete="off" placeholder="M5H 2M9" />
            </Form.Item>
          </div>
          {error && !matches ? <div className="ir-new-patient-error" role="alert">{error}</div> : null}
          <div className="ir-new-patient-actions">
            <Button disabled={saving} onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>Add patient</Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
